import * as ProgressPrimitive from '@radix-ui/react-progress';
import * as React from 'react';

import { cn } from '@/lib/utils';

interface ProgressProps extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  /** Color variant for the progress bar */
  variant?: 'default' | 'health' | 'mana' | 'experience' | 'stamina';
}

const variantStyles = {
  default: {
    indicator: 'bg-gradient-to-r from-biolume-teal to-biolume-cyan',
    glow: 'shadow-[0_0_10px_rgba(34,211,238,0.5)]',
  },
  health: {
    indicator: 'bg-gradient-to-r from-red-600 to-game-health',
    glow: 'shadow-[0_0_10px_rgba(248,113,113,0.5)]',
  },
  mana: {
    indicator: 'bg-gradient-to-r from-blue-600 to-game-mana',
    glow: 'shadow-[0_0_10px_rgba(96,165,250,0.5)]',
  },
  experience: {
    indicator: 'bg-gradient-to-r from-yellow-600 to-game-experience',
    glow: 'shadow-[0_0_10px_rgba(251,191,36,0.5)]',
  },
  stamina: {
    indicator: 'bg-gradient-to-r from-green-600 to-game-stamina',
    glow: 'shadow-[0_0_10px_rgba(74,222,128,0.5)]',
  },
};

const Progress = React.forwardRef<React.ElementRef<typeof ProgressPrimitive.Root>, ProgressProps>(
  ({ className, value, variant = 'default', ...props }, ref) => {
    const styles = variantStyles[variant];

    return (
      <ProgressPrimitive.Root
        ref={ref}
        className={cn(
          // Energy bar container styling
          'relative h-3 w-full overflow-hidden rounded-full',
          // Dark inset background
          'bg-abyss-dark/80',
          // Border with subtle glow
          'border border-border/50',
          // Inner shadow for depth
          'shadow-[inset_0_2px_4px_rgba(0,0,0,0.4)]',
          className
        )}
        {...props}
      >
        <ProgressPrimitive.Indicator
          className={cn(
            'h-full flex-1 rounded-full transition-all duration-300',
            // Gradient fill
            styles.indicator,
            // Glow effect
            styles.glow,
            // Shine effect overlay
            'relative overflow-hidden',
            // Top highlight
            'before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/20 before:to-transparent before:h-1/2'
          )}
          style={{ width: `${String(value ?? 0)}%` }}
        />
        {/* Segment lines for game feel */}
        <div className="absolute inset-0 flex pointer-events-none">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="flex-1 border-r border-black/20 last:border-r-0" />
          ))}
        </div>
      </ProgressPrimitive.Root>
    );
  }
);
Progress.displayName = ProgressPrimitive.Root.displayName;

export { Progress };
